import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CustomAutocomplete from '../../atoms/CustomAutocomplete';
import { useGeneralContext } from '../../../../context/GeneralContext';
import { useGetAll } from '../../../../hooks/useGetAll';

export const ADM = 'Administrator';

const UserGroupSelect = ({
  setFieldValue,
  setFieldTouched,
  handleBlur,
  touched,
  errors,
  value,
  disabled,
  disableFillAdmin,
  resetForm,
  initialValues,
  info,
  labelInside,
  hideSelectAll,
}) => {
  const {
    data: { user_group },
    isFetching,
  } = useGetAll('user_group');
  const { openToast } = useGeneralContext();
  const { t } = useTranslation();

  const userGroups = initialValues?.length > 0 ? initialValues : user_group;

  useEffect(() => {
    if (
      !isFetching &&
      userGroups?.length > 0 &&
      value.length > 0 &&
      value.filter(v => !userGroups?.find(ug => ug.value === v)).length > 0
    ) {
      setFieldTouched('user_group', true, false);
      setFieldValue(
        'user_group',
        value.filter(v => userGroups?.find(ug => ug.value === v)),
        false,
      );
    }
  }, [isFetching, setFieldTouched, setFieldValue, userGroups, value]);

  useEffect(() => {
    if (
      !disableFillAdmin &&
      !isFetching &&
      userGroups?.length > 0 &&
      value.length === 0 &&
      userGroups?.find(user => user.value === ADM)
    ) {
      if (resetForm && initialValues) {
        setTimeout(() => {
          resetForm({
            values: {
              ...initialValues,
              user_group: userGroups?.find(user => user.value === ADM)
                ?.hasAccess
                ? [ADM]
                : [ADM].concat(
                    userGroups.filter(ug => ug.hasAccess).map(ug => ug.value),
                  ),
            },
          });
        }, 100);
      } else {
        setFieldValue(
          'user_group',
          userGroups?.find(user => user.value === ADM)?.hasAccess
            ? [ADM]
            : [ADM].concat(
                userGroups.filter(ug => ug.hasAccess).map(ug => ug.value),
              ),
          false,
        );
      }
    }
  }, [
    disableFillAdmin,
    initialValues,
    isFetching,
    resetForm,
    setFieldValue,
    userGroups,
    value,
  ]);

  const handleUsersChange = (tags, reason) => {
    if (!disableFillAdmin && reason === 'clear') {
      setFieldValue(
        'user_group',
        [ADM].concat(
          userGroups
            .filter(
              ug =>
                ug.value !== ADM && !ug.hasAccess && value.includes(ug.value),
            )
            ?.map(ug => ug.label),
        ),
      );
    } else if (!disableFillAdmin && !tags.includes(ADM)) {
      openToast(
        t('molecules.user_group_select.warning_administrator'),
        'warning',
      );
    } else if (disableFillAdmin && tags.includes(ADM)) {
      setFieldValue('user_group', [ADM]);
    } else if (reason === 'clear') {
      setFieldValue(
        'user_group',
        tags.concat(
          userGroups
            .filter(
              ug =>
                ug.value !== ADM && !ug.hasAccess && value.includes(ug.value),
            )
            ?.map(ug => ug.label),
        ),
      );
    } else {
      setFieldValue('user_group', tags);
    }
  };

  return (
    <CustomAutocomplete
      id="user_group"
      labelInside={labelInside}
      placeholder={
        labelInside ? t('molecules.user_group_select.text_input') : null
      }
      label={t('molecules.user_group_select.text_input')}
      info={info || t('molecules.user_group_select.info')}
      required
      loading={isFetching}
      tags={
        userGroups
          .filter(ug => ug.hasAccess || value.includes(ug.value))
          ?.map(ug => ug.label) || []
      }
      selected={value}
      onBlur={handleBlur}
      onSelectTags={handleUsersChange}
      touched={touched.user_group}
      error={errors.user_group}
      disabled={disabled}
      deletableTag={opt => {
        const hasAccess = userGroups?.find(ug => ug.value === opt)?.hasAccess;
        if (!hasAccess) return false;
        if (disableFillAdmin) return !value.includes(ADM) || opt === ADM;
        return opt !== ADM;
      }}
      hideSelectAll={hideSelectAll}
    />
  );
};

UserGroupSelect.defaultProps = {
  disabled: false,
  disableFillAdmin: false,
  initialValues: null,
  resetForm: null,
  info: null,
  handleBlur: () => null,
  setFieldTouched: () => null,
  touched: {},
  errors: {},
  labelInside: false,
  hideSelectAll: false,
};

UserGroupSelect.propTypes = {
  errors: PropTypes.shape({
    user_group: PropTypes.string,
  }),
  value: PropTypes.any.isRequired,
  initialValues: PropTypes.any,
  disabled: PropTypes.bool,
  info: PropTypes.string,
  disableFillAdmin: PropTypes.bool,
  handleBlur: PropTypes.any,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func,
  resetForm: PropTypes.func,
  touched: PropTypes.shape({
    user_group: PropTypes.any,
  }),
  labelInside: PropTypes.bool,
  hideSelectAll: PropTypes.bool,
};

export default UserGroupSelect;
